import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * MultiTimeframeConfluence — 3-Tick Trend Sync
 *
 * How it works:
 * - Checks the internal trend state (isBullishBase/isBearishBase or Dominant Base) across the last 3 historical snapshots.
 * - If the trend is consistently Bullish or Bearish across all 3 intervals without wavering, 
 *   it confirms a High Conviction trend (filters out chop/noise).
 */
export const evaluateMultiTimeframeConfluence: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const trendAnalysisResult = context.liveIndicators?.trendAnalysisData;
    // fullTrendData is an array of rows with pre-calculated `isBullishBase`, `isBearishBase`
    const fullTrendData = Array.isArray(trendAnalysisResult) ? trendAnalysisResult : (trendAnalysisResult?.data || []);
    
    // Require at least 3 historical points to form a confluence
    if (fullTrendData.length < 3) return null;

    const t0 = fullTrendData[0];
    const t1 = fullTrendData[1];
    const t2 = fullTrendData[2];
    
    const isBullConfluence = t0.isBullishBase && t1.isBullishBase && t2.isBullishBase;
    const isBearConfluence = t0.isBearishBase && t1.isBearishBase && t2.isBearishBase;

    if (!isBullConfluence && !isBearConfluence) {
        return null; // Mixed or choppy
    }

    const { symbol, expiry, gap } = context;
    const spot = t0.spotPrice || t0.index_ltp || t0.ltp || data.spotPrice || data.ltp || 0;
    if (spot <= 0) return null;

    const liveRawOptsArr = t0.flatOpts || extractSnapshotOptions(t0, symbol, expiry) || [];
    const liveFlatOpts = ensureFlatOpts(liveRawOptsArr);
    if (!liveFlatOpts.length) return null;

    const atmStrike = Math.round(spot / gap) * gap;
    const optionType = isBullConfluence ? 'CE' : 'PE';

    // Buy ATM option in direction of confluence
    const atmOpt = liveFlatOpts.find((o: any) => o.strike === atmStrike && o.type === optionType);
    if (!atmOpt) return null;

    const entryPrice = atmOpt.ltp || atmOpt.price || atmOpt.close || 0;
    if (entryPrice <= 0) return null;

    return {
        trade_type: isBullConfluence ? 'BULL' : 'BEAR',
        strike: atmStrike,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `HIGH CONVICTION: Multi-Timeframe ${isBullConfluence ? 'BULL' : 'BEAR'} Sync`
    };
};
